import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface OnboardingState {
  step: string;
  interestPath: string | null;
  workType: string | null;
  businessName: string;
  overlapAllowed: boolean;
  demoSeeded: boolean;
  hasActiveSubscription: boolean;
  completed: boolean;
}

export interface CompleteProfile {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export function useOnboarding(enabled = true) {
  return useQuery({
    queryKey: ['onboarding'],
    queryFn: async () => (await apiClient.get<OnboardingState>('/api/v1/onboarding')).data,
    staleTime: 10_000,
    enabled
  });
}

function useStateMutation<T>(fn: (p: T) => Promise<OnboardingState>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => qc.setQueryData(['onboarding'], data)
  });
}

export const useUpdateBusiness = () =>
  useStateMutation(async (p: { businessName: string; workType?: string }) =>
    (await apiClient.put<OnboardingState>('/api/v1/onboarding/business', p)).data);

export const useUpdateHow = () =>
  useStateMutation(async (p: { overlapAllowed: boolean }) =>
    (await apiClient.put<OnboardingState>('/api/v1/onboarding/how', p)).data);

export const useUpdateInterest = () =>
  useStateMutation(async (p: { interestPath: string }) =>
    (await apiClient.put<OnboardingState>('/api/v1/onboarding/interest', p)).data);

export const useSeedDemo = () =>
  useStateMutation(async () =>
    (await apiClient.post<OnboardingState>('/api/v1/onboarding/seed-demo', {})).data);

export const useCompleteOnboarding = () =>
  useStateMutation(async (p: CompleteProfile) =>
    (await apiClient.post<OnboardingState>('/api/v1/onboarding/complete', p)).data);

/** Step 5 checkout — reuses the billing session with the onboarding landing paths. */
export function useStartOnboardingCheckout() {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ url: string }>('/api/v1/billing/checkout-session?flow=onboarding', {})).data,
    onSuccess: (data) => window.location.assign(data.url)
  });
}
